"use client";

import { GetOnlineUsersResponseDTO, OnlineUser } from "@/app/api/users/online/online-users.dto";
import {
	COMMENTS_API,
	CONNECTIONS_API,
	ONLINE_USERS_API,
	PRODUCT_VOTES_API,
} from "@/lib/constants";
import { TGetProductCommentsCount, useGlobalStore } from "@/store/global-store";
import { TGetVotes } from "@/types/votes";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect } from "react";
import { useAuth } from "./auth-provider";

type TGetConnections = {
	followingProfiles: string[];
	followerProfiles: string[];
	followingCategories: string[];
	followingFamilies: string[];
	followingChannels: string[];
};

type GlobalStoreContext = {
	numberOfUnreadChats: number;
	onlineUsers: {
		users: OnlineUser[];
		isError: boolean;
		isLoading: boolean;
	};
};

const GlobalStoreContext = createContext<GlobalStoreContext | undefined>(undefined);

const fetchOnlineUsers = async () => {
	try {
		const response = await fetch(ONLINE_USERS_API, { cache: "no-store" });

		const { data } = (await response.json()) as { data?: GetOnlineUsersResponseDTO };

		return data;
	} catch (error) {
		console.log(error);

		return {
			onlineUsers: [],
			numberOfUnreadChats: 0,
		};
	}
};

const GlobalStore = ({ children }: { children: React.ReactNode }) => {
	const {
		setProductsCommentCount,
		setFollowingProfiles,
		setFollowingParentCategories,
		setFollowingFamilies,
		setFollowingChannels,
		setUserVotes,
		setProductVotes,
	} = useGlobalStore();

	const { isLoggedIn } = useAuth();

	const {
		isLoading: isOnlineUsersLoading,
		isError: isOnlineUsersError,
		data,
	} = useQuery({
		queryKey: ["onlineUsers"],
		queryFn: fetchOnlineUsers,
	});

	const onlineUsers = data?.onlineUsers || [];
	const numberOfUnreadChats = data?.numberOfUnreadChats || 0;

	// Fetches following profiles, followers, following categories of the current user
	const fetchConnections = useCallback(async () => {
		try {
			const response = await fetch(CONNECTIONS_API, {
				next: {
					revalidate: 600, // 10 minutes
				},
			});
			if (response.ok) {
				const { data } = (await response.json()) as { data: TGetConnections };
				setFollowingProfiles(data.followingProfiles);
				setFollowingParentCategories(data.followingCategories);
				setFollowingFamilies(data.followingFamilies);
				setFollowingChannels(data.followingChannels);
			}
		} catch (error) {
			console.log(error);
		}
	}, [
		setFollowingProfiles,
		setFollowingParentCategories,
		setFollowingFamilies,
		setFollowingChannels,
	]);

	// Fetches productIds of products upvoted by the user
	const fetchUserVotes = useCallback(async () => {
		try {
			const response = await fetch(PRODUCT_VOTES_API, { method: "GET" });
			if (response.ok) {
				const { data } = (await response.json()) as { data: { userVotes: string[] } };
				setUserVotes(data.userVotes);
			}
		} catch (error) {
			console.log(error);
		}
	}, [setUserVotes]);

	// Fetches product comments count
	const fetchProductCommentsCount = useCallback(async () => {
		try {
			const res = await fetch(`${COMMENTS_API}/count`, {
				next: {
					revalidate: 60, // 1 minute
				},
			});
			if (res.ok) {
				const { data } = (await res.json()) as { data: TGetProductCommentsCount };
				setProductsCommentCount(data);
			}
		} catch (error) {
			console.log(error);
		}
	}, [setProductsCommentCount]);

	// Fetches total count of the product votes
	const fetchProductVotes = useCallback(async () => {
		try {
			const response = await fetch(PRODUCT_VOTES_API, {
				next: {
					revalidate: 10, // 10 seconds
				},
			});
			if (response.ok) {
				const { data } = (await response.json()) as { data: { votes: TGetVotes } };
				setProductVotes(data.votes);
			}
		} catch (error) {
			console.log(error);
		}
	}, [setProductVotes]);

	useEffect(() => {
		if (isLoggedIn) {
			fetchConnections();
			fetchUserVotes();
		}
	}, [isLoggedIn, fetchConnections, fetchUserVotes]);

	useEffect(() => {
		fetchProductCommentsCount();
		fetchProductVotes();
	}, [fetchProductCommentsCount, fetchProductVotes]);

	return (
		<GlobalStoreContext.Provider
			value={{
				numberOfUnreadChats,
				onlineUsers: {
					users: onlineUsers,
					isError: isOnlineUsersError,
					isLoading: isOnlineUsersLoading,
				},
			}}
		>
			{children}
		</GlobalStoreContext.Provider>
	);
};

export const useStore = () => {
	const context = useContext(GlobalStoreContext);
	if (context === undefined) {
		throw new Error("useStore must be used within a GlobalStoreContext");
	}

	return context;
};

export default GlobalStore;
